/* MySchoolRx — About page. Dedicated MySchoolRx content (operated by 589 Labs LLC). */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicPageShell, useLandingCta, ctaHref, BLUE, BLUE_HOVER } from "./publicChrome";

const PILLARS = [
  "Instruction",
  "Administrative Leadership",
  "Fiscal",
  "Governance",
  "Compliance",
  "Student Performance",
  "School Management",
  "Mission and Key Design Elements",
];

const Section = ({ children, className = "" }) => (
  <section className={`mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const H2 = ({ children }) => (
  <h2 className="text-[26px] sm:text-[30px] font-bold text-[#111827] tracking-tight leading-tight">{children}</h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[18px] leading-relaxed text-[#4b5563]">{children}</p>
);

const MySchoolRxAbout = () => {
  const navigate = useNavigate();
  const cta = useLandingCta();
  return (
    <PublicPageShell
      title="About MySchoolRx | 589 Labs LLC"
      description="MySchoolRx brings school conditions into a structured MySchoolRx across eight pillars — helping leadership teams and governing boards organize evidence and act on priorities."
      canonicalPath="/about"
      testid="about-page"
    >
      {/* Hero */}
      <div className="border-b border-[#e5e7eb] bg-[#f9fbfc]">
        <Section className="py-14 sm:py-20">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] tracking-tight leading-[1.08]" data-testid="about-title">
            About MySchoolRx
          </h1>
          <p className="mt-5 text-[20px] sm:text-[22px] text-[#4b5563] leading-relaxed">
            A clearer picture of school health. A more focused path forward.
          </p>
        </Section>
      </div>

      <Section className="py-12 sm:py-16">
        <H2>Why MySchoolRx exists</H2>
        <P>
          School leaders and governing boards make important decisions with information spread across reports,
          spreadsheets, policies, and conversations. Seeing how those pieces fit together—and where action is most
          urgent—can be difficult.
        </P>
        <P>
          MySchoolRx brings that information into a structured MySchoolRx. It helps leadership teams
          examine school conditions across eight pillars, identify areas requiring attention, and turn their assessment
          into a report that supports informed discussion and practical next steps.
        </P>
      </Section>

      <div className="bg-[#f9fbfc] border-y border-[#e5e7eb]">
        <Section className="py-12 sm:py-16">
          <H2>Eight pillars. One school-quality picture.</H2>
          <P>MySchoolRx examines:</P>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="about-pillars">
            {PILLARS.map((p) => (
              <li key={p} className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-4 py-3">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: BLUE }} />
                <span className="text-[17px] font-semibold text-[#111827]">{p}</span>
              </li>
            ))}
          </ul>
          <P>Together, these pillars provide a broader view than any single performance measure.</P>
        </Section>
      </div>

      <Section className="py-12 sm:py-16">
        <H2>Built for useful conversations</H2>
        <P>
          MySchoolRx supports school leadership teams, governing boards, and organizations responsible for overseeing or
          supporting schools.
        </P>
        <P>
          Schools can assess their own conditions. Where enabled, authorized Portfolio teams can complete a separate
          assessment and compare the two perspectives. Differences become a starting point for discussion: What evidence
          supports each view? Where is there agreement? What needs a closer look?
        </P>
      </Section>

      <div className="bg-[#f9fbfc] border-y border-[#e5e7eb]">
        <Section className="py-12 sm:py-16">
          <H2>Research informs the approach</H2>
          <P>
            The Diagnostic draws on doctoral research into school quality and uses a structured, weighted assessment
            approach. Its purpose is to help leaders organize evidence, recognize priorities, and ask better questions.
          </P>
          <P>
            The Diagnostic supports professional judgment. It does not replace an authorizer&apos;s evaluation, an
            accreditation review, a financial audit, or a formal compliance determination.
          </P>
        </Section>
      </div>

      <Section className="py-12 sm:py-16">
        <H2>Help shape what comes next</H2>
        <P>
          Our Founding Partner program invites school and Portfolio leaders to use MySchoolRx and share practical
          feedback. Their experience helps us improve usability, reporting, and the value of the platform.
        </P>
        <p className="mt-4 text-[18px] font-semibold text-[#111827]">Free for a limited time. No payment required.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => navigate(ctaHref(cta))}
            size="lg"
            className="h-12 text-[18px] text-white px-6 w-full sm:w-auto"
            style={{ backgroundColor: BLUE }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
            data-testid="about-cta-primary"
          >
            Become a Founding Partner <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <a
            href="/contact"
            className="inline-flex items-center justify-center h-12 px-6 rounded-md border border-[#d1d5db] text-[18px] font-semibold text-[#111827] hover:bg-[#f9fafb] w-full sm:w-auto"
            data-testid="about-cta-contact"
          >
            Contact the MySchoolRx team
          </a>
        </div>
        <p className="mt-10 text-[15px] text-[#6b7280]" data-testid="about-operator-line">
          MySchoolRx is operated by 589 Labs LLC.
        </p>
      </Section>
    </PublicPageShell>
  );
};

export default MySchoolRxAbout;
